import React, { memo, useCallback, useMemo } from 'react';
import { Tooltip } from '@planne-software/uni/Tooltip';
import { ScopedCssBaseline } from '@planne-software/uni/mui/material';

import { convertIsoDateToShortDate } from '@src/common';
import { IconCircleCheckFilled, IconCalendar, IconUser, IconClock } from '@tabler/icons-react';
import { TariffsPerType } from '@src/stores/product';
import { Product } from '@src/core/buyerApiClient';

import {
  ImageContainer,
  ProductContainer,
  ProductContentArea,
  ProductName,
  ProductNameWrapper,
  ProductProviderName,
  ProductSelectionInfo,
  SelectionInfo,
  TariffContainer,
} from './styles';
import Chip from '@src/components/reusable/Chip';
import useTranslate from '@src/i18n/useTranslate';
import { useWidgetsTheme } from '@src/core/hooks';

interface AdderProductCardProps {
  product: Product;
  isSelected: boolean;
  isProductFilled: boolean;
  selectedDate?: string;
  selectedHour?: string;
  selectedTariffs?: TariffsPerType[];
  onClick: (productId: string) => void;
  disabled?: boolean;
  appId?: string;
}

const AdderProductCard = (props: Readonly<AdderProductCardProps>) => {
  const {
    isProductFilled,
    product,
    selectedDate,
    selectedHour,
    isSelected,
    selectedTariffs,
    onClick,
    disabled,
    appId,
  } = props;

  const { i18n } = useTranslate();
  const actionColor = useWidgetsTheme('actionColor');

  const tariffs = useMemo(() => {
    if (!selectedTariffs || selectedTariffs.length === 0) return;

    const tariffsToShow = selectedTariffs.slice(0, 2);
    const tariffsToHide = selectedTariffs.slice(2);

    return { tariffsToShow, tariffsToHide };
  }, [selectedTariffs]);

  const formatTariffs = useCallback(
    (tariffsToFormat: TariffsPerType[]) =>
      tariffsToFormat.map((tariff) => `${tariff.selectedQuantity}x ${tariff.type.name}`).join(', '),
    [],
  );

  return (
    <ProductContainer
      key={product.id}
      $isSelected={isSelected}
      $isCompleted={isProductFilled}
      onClick={() => onClick(product.id)}
      aria-disabled={disabled}
    >
      <ImageContainer>{product.thumbnailUrl ? <img src={product.thumbnailUrl} alt='' /> : null}</ImageContainer>

      <ProductContentArea>
        <ProductNameWrapper>
          <ProductName>{product.name}</ProductName>

          {isProductFilled ? <IconCircleCheckFilled size={24} style={{ flexShrink: 0 }} color={actionColor} /> : null}
        </ProductNameWrapper>

        {appId !== undefined && product.appId !== appId ? (
          <ScopedCssBaseline>
            <Tooltip label={i18n('product_package_adder_sold_by', { owner: product.app.name })} placement='top' arrow>
              <ProductProviderName>
                {i18n('product_package_adder_sold_by', { owner: product.app.name })}
              </ProductProviderName>
            </Tooltip>
          </ScopedCssBaseline>
        ) : null}

        <ProductSelectionInfo>
          <IconCalendar size={20} color='#464646' />

          {selectedDate ? <SelectionInfo>{convertIsoDateToShortDate(selectedDate)}</SelectionInfo> : null}
        </ProductSelectionInfo>

        <ProductSelectionInfo>
          <IconUser size={20} color='#464646' />

          {tariffs ? (
            <TariffContainer>
              {formatTariffs(tariffs.tariffsToShow)}
              {tariffs.tariffsToHide.length > 0 ? (
                <div title={formatTariffs(tariffs.tariffsToHide)}>
                  <Chip color='neutral' label={`+${tariffs.tariffsToHide.length}`} />
                </div>
              ) : null}
            </TariffContainer>
          ) : null}
        </ProductSelectionInfo>

        <ProductSelectionInfo>
          <IconClock size={20} color='#464646' />

          {selectedHour ? <SelectionInfo>{selectedHour}</SelectionInfo> : null}
        </ProductSelectionInfo>
      </ProductContentArea>
    </ProductContainer>
  );
};

export default memo(AdderProductCard);
